import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, fmtEur, errDetail } from "../lib/api";
import { useApp } from "../lib/store";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet, Plus, Save, Trash2, Target, Sparkles, Send, CheckCircle2, XCircle, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";

const NAVY = "#1B2A4A", AMBER = "#F5A623";
const fmtQty = (v) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(Number(v) || 0);
const YEARS = (() => { const y = new Date().getFullYear(); return [y - 1, y, y + 1, y + 2]; })();

export function ForecastPanel({ accountId }) {
  const { t, canWrite, isManager } = useApp();
  const [year, setYear] = useState(new Date().getFullYear());
  const [scope, setScope] = useState("all");
  const [projects, setProjects] = useState([]);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [metric, setMetric] = useState("revenue");
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newOpp, setNewOpp] = useState("none");
  const [opps, setOpps] = useState([]);
  const [submission, setSubmission] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const fileRef = useRef();

  const editable = canWrite && scope !== "all";

  const loadProjects = useCallback(() => {
    if (!accountId) return;
    api.get(`/projects?account_id=${accountId}`).then((r) => setProjects(r.data)).catch(() => {});
  }, [accountId]);

  const loadSubmission = useCallback(() => {
    if (!accountId || scope === "all") { setSubmission(null); return; }
    api.get(`/forecast-submissions/one`, { params: { account_id: accountId, year, project_id: scope } })
      .then((r) => setSubmission(r.data)).catch(() => setSubmission(null));
  }, [accountId, year, scope]);

  const loadForecast = useCallback(() => {
    if (!accountId) return;
    api.get(`/account-forecasts`, { params: { account_id: accountId, year, project_id: scope } })
      .then((r) => { setData(r.data); setRows(r.data.rows.map((x) => ({ ...x }))); })
      .catch(() => {});
  }, [accountId, year, scope]);

  useEffect(() => { loadProjects(); }, [loadProjects]);
  useEffect(() => { loadForecast(); }, [loadForecast]);
  useEffect(() => { loadSubmission(); }, [loadSubmission]);

  const status = submission?.status || "none";
  const submitForecast = async () => {
    try {
      await api.post(`/forecast-submissions/submit`, { account_id: accountId, year, project_id: scope });
      toast.success(t("submitted_ok") + " ✓");
      loadSubmission();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const approveForecast = async () => {
    try {
      await api.post(`/forecast-submissions/approve`, { account_id: accountId, year, project_id: scope });
      toast.success(t("approved_ok") + " ✓");
      loadSubmission();
    } catch (e) { toast.error(errDetail(e)); }
  };
  const rejectForecast = async () => {
    try {
      await api.post(`/forecast-submissions/reject`, { account_id: accountId, year, project_id: scope, comment: rejectComment });
      toast.success(t("rejected_ok"));
      setRejectOpen(false); setRejectComment("");
      loadSubmission();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const setCell = (month, field, val) => {
    setRows((rs) => rs.map((r) => (r.month === month ? { ...r, [field]: val } : r)));
  };

  const save = async () => {
    try {
      await api.post(`/account-forecasts/save`, { account_id: accountId, year, project_id: scope, rows });
      toast.success(t("forecast_saved") + " ✓");
      loadForecast();
      loadSubmission();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const download = async (template = false) => {
    try {
      const r = await api.get(`/account-forecasts/export`, {
        params: { account_id: accountId, year, template }, responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `forecast_${year}${template ? "_template" : ""}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/account-forecasts/import?account_id=${accountId}`, fd);
      toast.success(`${t("import_done")}: ${r.data.imported}`);
      loadProjects(); loadForecast();
    } catch (err) { toast.error(errDetail(err)); }
    e.target.value = "";
  };

  const openAdd = async () => {
    setNewName(""); setNewOpp("none");
    try { const r = await api.get(`/accounts/${accountId}/summary`); setOpps(r.data.opportunities || []); } catch { setOpps([]); }
    setAddOpen(true);
  };

  const createProject = async () => {
    if (!newName.trim()) return;
    try {
      const r = await api.post(`/projects`, {
        account_id: accountId, name: newName.trim(),
        opportunity_id: newOpp !== "none" ? newOpp : "",
      });
      toast.success(t("project_created") + " ✓");
      setAddOpen(false);
      await loadProjects();
      setScope(r.data.id);
    } catch (e) { toast.error(errDetail(e)); }
  };

  const autofill = async () => {
    try {
      const r = await api.post(`/account-forecasts/autofill`, { account_id: accountId, year, project_id: scope });
      if (r.data.filled > 0) toast.success(`${t("autofilled")}: ${r.data.filled}`);
      else toast.info(t("no_autofill"));
      loadForecast();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const deleteProject = async () => {
    if (!window.confirm(t("delete_project") + "?")) return;
    try {
      await api.delete(`/projects/${scope}`);
      toast.success(t("project_deleted"));
      setScope("all");
      loadProjects();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const totals = useMemo(() => {
    const fc = rows.reduce((s, r) => s + (Number(r[metric === "revenue" ? "fc_revenue" : "fc_qty"]) || 0), 0);
    const ac = rows.reduce((s, r) => s + (Number(r[metric === "revenue" ? "actual_revenue" : "actual_qty"]) || 0), 0);
    return { fc, ac, att: fc ? Math.round((ac / fc) * 1000) / 10 : 0 };
  }, [rows, metric]);

  const chart = rows.map((r) => ({
    label: r.month.slice(5),
    forecast: Number(r[metric === "revenue" ? "fc_revenue" : "fc_qty"]) || 0,
    actual: Number(r[metric === "revenue" ? "actual_revenue" : "actual_qty"]) || 0,
  }));
  const fmt = metric === "revenue" ? fmtEur : fmtQty;

  return (
    <div className="space-y-4" data-testid="forecast-panel">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-44 h-9 bg-white" data-testid="forecast-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{t("fiscal_year")} {y}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-60 h-9 bg-white" data-testid="forecast-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all_projects")}</SelectItem>
            <SelectItem value="customer">{t("customer_level")}</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={metric} onValueChange={setMetric}>
          <SelectTrigger className="w-36 h-9 bg-white" data-testid="forecast-metric-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="revenue">{t("revenue")} (€)</SelectItem>
            <SelectItem value="qty">{t("quantity")}</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {canWrite && (
          <Button variant="outline" size="sm" onClick={openAdd} data-testid="add-project-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" />{t("new_project")}
          </Button>
        )}
        {editable && projects.some((p) => p.id === scope) && (
          <Button variant="outline" size="sm" onClick={deleteProject} data-testid="delete-project-btn"
            className="text-red-600 hover:text-red-700">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => download(true)} data-testid="forecast-template-btn">
          <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />{t("download_template")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => download(false)} data-testid="forecast-export-btn">
          <Download className="h-3.5 w-3.5 mr-1.5" />{t("export_excel")}
        </Button>
        {canWrite && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onImport} data-testid="forecast-import-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="forecast-import-btn">
              <Upload className="h-3.5 w-3.5 mr-1.5" />{t("import_excel")}
            </Button>
          </>
        )}
        {editable && (
          <Button variant="outline" size="sm" onClick={autofill} data-testid="forecast-autofill-btn"
            className="text-[#1B2A4A] border-[#F5A623]/60">
            <Sparkles className="h-3.5 w-3.5 mr-1.5 text-[#F5A623]" />{t("autofill_actuals")}
          </Button>
        )}
      </div>

      {/* Approval status bar */}
      {scope !== "all" && (
        <div className="flex flex-wrap items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-2.5" data-testid="forecast-approval-bar">
          <span className="text-sm font-semibold text-gray-700">{t("approval_status")}:</span>
          <Badge data-testid="forecast-approval-status" className={`hover:bg-inherit ${
            status === "approved" ? "bg-emerald-100 text-emerald-700" :
            status === "submitted" ? "bg-blue-100 text-blue-700" :
            status === "rejected" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-600"}`}>
            {t(`st_${status}`)}
          </Badge>
          {submission?.review_comment && status === "rejected" && (
            <span className="text-xs text-red-500 italic">"{submission.review_comment}"</span>
          )}
          <div className="flex-1" />
          {canWrite && ["draft", "rejected", "none"].includes(status) && (submission?.has_data ?? true) && (
            <Button size="sm" onClick={submitForecast} data-testid="submit-forecast-btn"
              className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white">
              <Send className="h-3.5 w-3.5 mr-1.5" />{t("submit_for_approval")}
            </Button>
          )}
          {isManager && status === "submitted" && (
            <>
              <Button size="sm" onClick={approveForecast} data-testid="approve-forecast-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />{t("fc_approve")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRejectOpen(true)} data-testid="reject-forecast-btn" className="text-red-600 border-red-200 hover:bg-red-50">
                <XCircle className="h-3.5 w-3.5 mr-1.5" />{t("fc_reject")}
              </Button>
            </>
          )}
        </div>
      )}

      {/* Approval history timeline */}
      {scope !== "all" && submission?.history?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg px-4 py-3" data-testid="forecast-history">
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2 flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />{t("history")}
          </p>
          <ol className="space-y-2">
            {submission.history.slice().reverse().map((h, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" data-testid="history-event">
                <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${
                  h.action === "approved" ? "bg-emerald-500" : h.action === "rejected" ? "bg-red-500" : "bg-blue-500"}`} />
                <div>
                  <span className="font-medium text-gray-800">{h.user_name || "—"}</span>
                  <span className="text-gray-500"> · {t(`ev_${h.action}`)}</span>
                  <span className="text-gray-400 text-xs ml-1.5">{h.at ? new Date(h.at).toLocaleString() : ""}</span>
                  {h.comment && <p className="text-xs text-gray-500 italic">"{h.comment}"</p>}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[[t("total_forecast"), fmt(totals.fc)], [t("total_actual"), fmt(totals.ac)], [t("attainment"), `${totals.att}%`]].map(([label, val], i) => (
          <div key={label} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`forecast-kpi-${i}`}>
            <p className="text-xs uppercase tracking-widest text-gray-400 font-semibold flex items-center gap-1.5"><Target className="h-3.5 w-3.5" />{label}</p>
            <p className={`text-xl font-bold font-mono mt-1 ${i === 2 ? (totals.att >= 100 ? "text-emerald-600" : totals.att >= 60 ? "text-[#F5A623]" : "text-red-500") : "text-[#1B2A4A]"}`}>{val}</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold mb-4">{t("forecast_vs_actual")} · {metric === "revenue" ? t("revenue") : t("quantity")}</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F1F3" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => metric === "revenue" ? `${Math.round(v / 1000)}k` : fmtQty(v)} />
            <Tooltip formatter={(v) => fmt(v)} />
            <Legend />
            <Bar dataKey="forecast" name={t("total_forecast")} fill="#94A3B8" radius={[3, 3, 0, 0]} />
            <Bar dataKey="actual" name={t("total_actual")} fill={AMBER} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Editable table */}
      {!editable && <p className="text-xs text-gray-400">{t("aggregate_readonly")}</p>}
      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b bg-gray-50/60">
              <th className="px-3 py-2.5">{t("month")}</th>
              <th className="px-3 py-2.5 text-right">{t("fc_revenue")}</th>
              <th className="px-3 py-2.5 text-right">{t("actual_revenue")}</th>
              <th className="px-3 py-2.5 text-right">{t("fc_qty")}</th>
              <th className="px-3 py-2.5 text-right">{t("actual_qty")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month} className="border-b border-gray-50" data-testid={`forecast-row-${r.month}`}>
                <td className="px-3 py-1.5 font-mono text-gray-600">{r.month}</td>
                {["fc_revenue", "actual_revenue", "fc_qty", "actual_qty"].map((f) => (
                  <td key={f} className="px-3 py-1.5 text-right">
                    {editable ? (
                      <Input type="number" value={r[f] ?? 0} data-testid={`fc-input-${r.month}-${f}`}
                        onChange={(e) => setCell(r.month, f, e.target.value)}
                        className="h-8 w-28 ml-auto font-mono text-xs text-right" />
                    ) : (
                      <span className="font-mono text-gray-700">{f.includes("revenue") ? fmtEur(r[f]) : fmtQty(r[f])}</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editable && (
        <div className="flex justify-end">
          <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={save} data-testid="save-forecast-btn">
            <Save className="h-4 w-4 mr-1.5" />{t("save_forecast")}
          </Button>
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("new_project")}</DialogTitle>
            <DialogDescription className="sr-only">{t("add_project")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-sm text-gray-500">{t("project_name")}</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-project-name-input" className="bg-white" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm text-gray-500">{t("link_opportunity")}</label>
              <Select value={newOpp} onValueChange={setNewOpp}>
                <SelectTrigger data-testid="new-project-opp-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("no_opp_link")}</SelectItem>
                  {opps.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={createProject} data-testid="create-project-btn">{t("add_project")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("fc_reject")}</DialogTitle>
            <DialogDescription className="sr-only">{t("reject_reason")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Textarea value={rejectComment} onChange={(e) => setRejectComment(e.target.value)}
              placeholder={t("reject_reason")} rows={3} data-testid="reject-comment-input" className="bg-white" />
            <Button className="w-full bg-red-600 hover:bg-red-700 text-white" onClick={rejectForecast} data-testid="confirm-reject-btn">
              {t("fc_reject")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
